import * as Styled from "./PreviewEmailModal.styles";

import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import { calendar64, footer64, gatherDefault64, location64 } from "./images";
import { useEffect, useState } from "react";

import BaseModal from "../BaseModal/BaseModal";
import DOMPurify from "dompurify";
import { Room } from "../../types/room";
import dayjs from "dayjs";
import imageCompression from "browser-image-compression";
import { sanitizeAndPrepareEmailDescription } from "../../utils/utils";

type PreviewEmailModalProps = {
  open: boolean;
  onClose: () => void;
  formData: any;
  existingEventImageUrl?: string | null;
};

const PreviewEmailModal = ({
  open,
  onClose,
  formData,
  existingEventImageUrl,
}: PreviewEmailModalProps) => {
  const [eventImage64, setEventImage64] = useState<string>(gatherDefault64);
  const isDesktop = useMediaQuery((theme: any) =>
    theme.breakpoints.up("desktop")
  );

  function formatTime(dateTime: any, formatString: string): string {
    return dayjs(dateTime).format(formatString);
  }

  const sanitizedEventTitle = DOMPurify.sanitize(formData.title);
  const sanitizedEventDescription = sanitizeAndPrepareEmailDescription(
    formData.description
  );
  const sanitizedEventTime = DOMPurify.sanitize(
    `${formatTime(
      formData.startDateTime,
      "MMMM DD, YYYY • h:mm A"
    )} - ${formatTime(formData.endDateTime, "h:mm A")}`
  );
  const officeLocationMapping: { [key: string]: string } = {
    Boston: "399 Boylston St #1000, Boston, MA 02116",
    Hartford: "185 Asylum St #3201, Hartford, CT 06103",
    "Boston & Hartford (New England)":
      "399 Boylston St #1000, Boston, MA 02116 (Boston Office) & 185 Asylum St #3201, Hartford, CT 06103 (Hartford Office)",
  };

  const sanitizedLocationText = DOMPurify.sanitize(
    formData.format === "Virtual"
      ? "Virtual"
      : formData.eventType === "Off-site"
      ? formData.location
      : formData.eventType === "On-site" && formData.rooms.length === 0
      ? officeLocationMapping[formData.office] || formData.office
      : formData.rooms.map((room: Room) => room.name).join(", ")
  );

  const sanitizedLocation = formData.googleMapsLocationLink
    ? DOMPurify.sanitize(
        `<a href="${formData.googleMapsLocationLink}" target="_blank" rel="noopener noreferrer">${sanitizedLocationText}</a>`
      )
    : sanitizedLocationText;

  useEffect(() => {
    const compressAndConvertImage = async (imageFile: File) => {
      const options = {
        maxSizeMB: 3.0,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(imageFile, options);
      return await imageCompression.getDataUrlFromFile(compressedFile);
    };

    const processImage = async () => {
      // FileInput seeds a text/plain placeholder for saved drafts; only compress real images.
      const file = formData.eventImageFile as File | null;
      if (file instanceof File && file.type?.startsWith("image/")) {
        const base64Image = await compressAndConvertImage(file);
        setEventImage64(base64Image);
      } else {
        setEventImage64(existingEventImageUrl || gatherDefault64);
      }
    };

    processImage();
  }, [existingEventImageUrl, formData.eventImageFile]);

  const mobileEmailHtml = `
    <div class="container" style="width: 100%; max-width: 800px; margin: 0 auto;">
    <img src="${eventImage64}" class="header-image" style="width: 100%; height: auto; max-height: 250px; object-fit: cover; object-position: center;">
    <div class="title" style="font-size: 24px; font-weight: 700; margin: 20px 0; padding: 0 20px;">
    ${sanitizedEventTitle}
    </div>
    <div class="description" style="font-size: 16px; margin: 10px 0; padding: 0 20px;">
    ${sanitizedEventDescription}
    </div>
    <div class="join-section" style="font-size: 18px; font-weight: bold; margin: 16px 0; margin-left: 20px;">
    <span style="font-weight: lighter; font-size: 14px; font-style: italic">For more details and the calendar invite, make sure to sign up for this event on Gather using the button below.</span><br><br>
    <a href="https://gather.newengland.devslalom.com/events?eventId=" class="sign-up-button" style="display: inline-block; width: 40%; height: 48px; background-color: #0C62FB; color: white; font-size: 16px; font-weight: 700; text-align: center; line-height: 48px; border-radius: 50px; text-decoration: none; margin-top: 0px;">
    <span style="color: white;">Sign up</span>
    </a>
    </div>
    <div style="width: 100%; text-align:center; margin: 16px 0;">
    <div style="justify-content: center; align-items: stretch; max-width: 800px; margin: auto;">
    <div class="dark-mode" style="flex: 1 1 50%; background-color: #DEFF4D; color: #0C62FB; font-size: 16px; font-weight: bold; text-align: center; line-height: 1.2; border-radius: 5px; margin: 5px; box-sizing: border-box; padding: 10px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
    <div style="display: flex; align-items: center; justify-content: center; height: 50%;">
    <img style="width:25px; height:25px; margin-right:5px;" src="${calendar64}" alt="When Icon">
    <span style="font-weight:bold;">When?</span>
    </div>
    <span style="color: #000000; font-weight:400; font-size: 13px; margin-top: 10px; height: 50%;">${sanitizedEventTime}</span>
    </div>
    <div class="dark-mode" style="flex: 1 1 50%; background-color: #DEFF4D; color: #0C62FB; font-size: 16px; font-weight: bold; text-align: center; line-height: 1.2; border-radius: 5px; margin: 5px; box-sizing: border-box; padding: 10px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
    <div style="display: flex; align-items: center; justify-content: center; height: 50%;">
    <img style="width:25px; height:25px; margin-right:5px;" src="${location64}" alt="Where Icon">
    <span style="font-weight:bold;">Where?</span>
    </div>
    <span style="color: #000000; font-weight:400; font-size: 13px; margin-top: 10px; height: 50%;">${sanitizedLocation}</span>
    </div>
    </div>
    </div>
    <a href="https://gather.newengland.devslalom.com/events">
    <div class="footer" style="width: 100%; height: auto; max-height: 80px; background-color: #002FAF; padding: 10px 0; text-align: center; display: flex; align-items: center; justify-content: center;">
    <img src="data:image/png;base64,${footer64}"  style="width: auto; height: 100%; max-height: 80px; object-fit: contain;">
    </div>
    </a>
    </div>
  `;

  const emailHtml = `
    <div class="container" style="width: 100%; max-width: 800px; margin: 0 auto;">
        <img src="${eventImage64}" class="header-image" style="width: 100%; height: auto; max-height: 400px; object-fit: cover; object-position: center;">

    <div class="title" style="font-size: 24px; font-weight: 700; margin: 20px 0; padding: 0 20px;">
    ${sanitizedEventTitle}
    </div>
    <div class="description" style="font-size: 16px; margin: 10px 0; padding: 0 20px;">
    ${sanitizedEventDescription}
    </div>
    <div class="join-section" style="font-size: 18px; font-weight: bold; margin: 16px 0; margin-left: 20px;">
    <span style="font-weight: bold; font-size: 14px;">*In order to attend, make sure to sign up for this event on Gather using the button below.</span><br><br>
    <a href="https://gather.newengland.devslalom.com/events?eventId=" class="sign-up-button" style="display: inline-block; width: 40%; height: 48px; background-color: #0C62FB; color: white; font-size: 16px; font-weight: 700; text-align: center; line-height: 48px; border-radius: 50px; text-decoration: none; margin-top: 0px;">
    <span style="color: white;">Sign up</span>
    </a>
    </div>
    <div style="width: 100%; text-align:center; margin: 16px 0;">
    <div style="justify-content: center; align-items: stretch; max-width: 800px; margin: auto;">
    <div style="width: 100%; text-align:center; margin: 16px 0;">
    <div style="display: flex; justify-content: center; align-items: stretch; max-width: 800px; margin: auto;">
    <div class="dark-mode" style="flex: 1 1 50%; max-width: 50%; min-height: 84px; background-color: #DEFF4D; color: #0C62FB; font-size: 16px; font-weight: bold; text-align: center; line-height: 1.2; border-radius: 5px; margin: 5px; box-sizing: border-box; padding: 10px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
    <div style="display: flex; align-items: center; justify-content: center; height: 50%;">
    <img style="width:25px; height:25px; margin-right:5px;" src="${calendar64}" alt="When Icon">
    <span style="font-weight:bold;">When?</span>
    </div>
    <span style="color: #000000; font-weight:400; font-size: 13px; margin-top: 10px; height: 50%;">${sanitizedEventTime}</span>
    </div>
    <div class="dark-mode" style="flex: 1 1 50%; max-width: 50%; min-height: 84px; background-color: #DEFF4D; color: #0C62FB; font-size: 16px; font-weight: bold; text-align: center; line-height: 1.2; border-radius: 5px; margin: 5px; box-sizing: border-box; padding: 10px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
    <div style="display: flex; align-items: center; justify-content: center; height: 50%;">
    <img style="width:25px; height:25px; margin-right:5px;" src="${location64}" alt="Where Icon">
    <span style="font-weight:bold;">Where?</span>
    </div>
    <span style="color: #000000; font-weight:400; font-size: 13px; margin-top: 10px; height: 50%;">${sanitizedLocation}</span>
    </div>
    </div>
    </div>
    <a href="https://gather.newengland.devslalom.com/events">
    <div class="footer" style="width: 100%; height: auto; max-height: 86px; background-color: #002FAF; padding: 10px 0; text-align: center; display: flex; align-items: center; justify-content: center;">
    <img src="data:image/png;base64,${footer64}"  style="width: auto; height: 100%; max-height: 80px; object-fit: contain;">
    </div>
    </a>
    </div>
  `;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      overflow="scroll"
      desktopWidth="50%"
    >
      <>
        <Styled.ModalTitleContainer>
          <Typography variant="h2">Email Preview</Typography>
        </Styled.ModalTitleContainer>
        <Typography variant="modalBody">
          <strong>Note:</strong> Outlook uses a rendering engine that is
          different from most browsers, so the email may look slightly different
          when sent. This may include the font family used in the email.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            width: "100%",
            maxWidth: "800px",
            margin: "0 auto",
          }}
          dangerouslySetInnerHTML={
            isDesktop ? { __html: emailHtml } : { __html: mobileEmailHtml }
          }
        />
      </>
    </BaseModal>
  );
};

export default PreviewEmailModal;
